/**
 * CasesList — the real member conversations list, read LIVE from the back
 * office via `member.cases.list` (034 US1; 039 US1 cursor pagination).
 * Route-on-read; no local copy (FR-006).
 *
 * Extracted from the former `MyCases` route (retired with `/my-cases` in the
 * same 030 US3 change) so the unified single "Viestit" list (D-220(i): no
 * Saapuneet/Lähetetyt tabs) reuses the exact pagination / a11y / honest-state
 * behaviour rather than re-implementing it. The
 * `detailBase` (`/messages`) + optional `analyticsRoute` are kept as reuse seams
 * (the sole live consumer is Viestit).
 *
 * 039 US1 — cursor pagination (infinite scroll):
 *   - The FIRST page suspends inside the caller's Suspense boundary.
 *   - A focusable "Load more" control is ALWAYS rendered while more cases
 *     exist — it IS the keyboard / screen-reader path (FR-003); an
 *     `IntersectionObserver` sentinel triggers the same load for pointer
 *     users (pure enhancement).
 *   - "Load more" appends the next page WITHOUT re-suspending — a failed
 *     load keeps the loaded cases and offers a retry (FR-005); nothing is
 *     mirrored across a reload (FR-006).
 *   - A polite live region announces "N more loaded" / "all cases shown".
 *
 * 055 prototype convergence — each row now shows ONLY the subject (with a
 * fallback when null/empty) and the back-office status pill, rendered as-is and
 * never re-mapped (P7). The creation date and the category are no longer shown
 * on a row (FR-011). Both stay in the projection; the category is rendered on no
 * member surface (D-229(b)) and the creation date only in the detail's ContactSummary.
 *
 * A row also carries the back office's per-conversation unread signal
 * (`hasUnreadReplies`, FR-015a). This REVERSES the earlier no-inline-unread
 * decision, amended deliberately in D-230 — see that entry before removing it.
 * Unread is shown with three simultaneous cues (tint, labelled dot, bolder
 * subject) so it never rests on colour alone, and is read fresh every time:
 * nothing is counted, accumulated, or stored here (FR-015b).
 */

import type { AnalyticsMemberRouteKey } from '@omaplus/shared-types';
import { type JSX, type ReactNode, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';

import { AnalyticsRouteReady } from '../analytics/AnalyticsCaptureProvider.js';
import { fill } from '../i18n/fill.js';
import { useLanguage } from '../i18n/useLanguage.js';
import { useTranslate } from '../i18n/useTranslate.js';
import { ConversationStatusPill } from './ConversationStatusPill.js';
import { useCases } from './useCases.js';

// ---------- Styles ----------

const noteClasses = 'text-sm text-muted-foreground';
// The card chrome lives on the activatable Link (the whole card is clickable).
// 055: matched to the prototype's row — an unread conversation is tinted with
// the brand primary, a read one sits on the plain card surface.
const cardLinkBaseClasses =
  'block rounded-xl border p-4 mb-3 no-underline text-inherit transition ' +
  'hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring';
const unreadCardClasses = 'border-primary/20 bg-primary/5';
const readCardClasses = 'border-border bg-card';
const loadMoreButtonClasses =
  'mt-2 inline-flex items-center rounded-md border border-border px-3 py-1.5 text-sm font-medium text-foreground disabled:opacity-50';

// ---------- Shared honest states ----------

export function CasesLoading(): JSX.Element {
  const t = useTranslate();
  // Announce the load→list transition (WCAG 4.1.3), matching the detail fallback.
  return (
    <p className={noteClasses} role="status" aria-live="polite">
      {t('myCases.loading')}
    </p>
  );
}

export function CasesUnavailable(): JSX.Element {
  const t = useTranslate();
  // No fabricated content — an honest "try again later", never sample data (FR-011).
  return <p className={noteClasses}>{t('myCases.unavailable')}</p>;
}

export interface CasesListProps {
  /** The route base a row links to (`/messages`). A row targets `${detailBase}/${id}`. */
  readonly detailBase: string;
  /**
   * Optional empty-state node so the host can supply a consistent treatment
   * (e.g. the Viestit illustrated panel). Falls back to a plain honest note.
   */
  readonly emptyState?: ReactNode;
  /**
   * Analytics route key surfaced when the list is ready. OMIT when the host
   * route already declares a route-level `analyticsPageKey` (e.g. `/messages`),
   * so readiness is not fired twice.
   */
  readonly analyticsRoute?: AnalyticsMemberRouteKey;
}

/**
 * The suspending real-cases list body. Renders inside a
 * `PortErrorBoundary(memberCases)` + `Suspense` the caller provides.
 */
export function CasesList({ detailBase, analyticsRoute, emptyState }: CasesListProps): JSX.Element {
  const t = useTranslate();
  const { active } = useLanguage();
  const view = useCases(active);

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const endStatusRef = useRef<HTMLParagraphElement | null>(null);
  // Track whether the member has ever been offered more pages, so the terminal
  // "all cases shown" affordance appears only after real pagination.
  const everHadMoreRef = useRef(false);
  const prevHasMoreRef = useRef(false);
  // Announce net new arrivals in a polite live region ("N more loaded").
  const prevCountRef = useRef(0);
  const [announcement, setAnnouncement] = useState('');

  const hasMore = view.status === 'ok' ? view.hasMore : false;
  const loadingMore = view.status === 'ok' ? view.loadingMore : false;
  const loadMoreError = view.status === 'ok' ? view.loadMoreError : false;
  const count = view.status === 'ok' ? view.cases.length : 0;

  if (hasMore) {
    everHadMoreRef.current = true;
  }

  // IntersectionObserver enhancement (pointer users). The visible "Load more"
  // button is the accessible fallback and the observer is enhancement-only.
  const loadMore = view.status === 'ok' ? view.loadMore : undefined;
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (sentinel === null || loadMore === undefined || !hasMore) {
      return;
    }
    if (typeof IntersectionObserver === 'undefined') {
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          loadMore();
        }
      }
    });
    observer.observe(sentinel);
    return () => {
      observer.disconnect();
    };
  }, [loadMore, hasMore]);

  // Announce "N more loaded" when the list grows (skip the initial render); at
  // the end, move focus to the terminal status so keyboard focus is not lost.
  useEffect(() => {
    const grew = count > prevCountRef.current && prevCountRef.current > 0;
    prevCountRef.current = count;
    if (grew && hasMore) {
      setAnnouncement(fill(t('myCases.moreLoaded'), { count }));
    }
    const justCompleted = prevHasMoreRef.current && !hasMore;
    prevHasMoreRef.current = hasMore;
    if (justCompleted && endStatusRef.current !== null) {
      endStatusRef.current.focus();
    }
  }, [count, hasMore, t]);

  if (view.status === 'unavailable') {
    return <CasesUnavailable />;
  }
  if (view.cases.length === 0) {
    // A member with nothing to show — a clear empty state, never an error or
    // sample data (FR-011).
    return (
      <>
        {analyticsRoute !== undefined ? (
          <AnalyticsRouteReady pageKey={{ kind: 'memberRoute', route: analyticsRoute }} />
        ) : null}
        {emptyState ?? <p className={noteClasses}>{t('myCases.empty')}</p>}
      </>
    );
  }

  return (
    <>
      {analyticsRoute !== undefined ? (
        <AnalyticsRouteReady pageKey={{ kind: 'memberRoute', route: analyticsRoute }} />
      ) : null}
      <ul>
        {view.cases.map((c) => {
          // 055 FR-015a — unread is read straight from the back office's own
          // signal on every read. Absent/undefined means READ: unread state is
          // never assumed, and nothing is counted or stored here (FR-015b).
          const unread = c.hasUnreadReplies === true;
          return (
            <li key={c.id}>
              <Link
                className={`${cardLinkBaseClasses} ${unread ? unreadCardClasses : readCardClasses}`}
                to={`${detailBase}/${encodeURIComponent(c.id)}`}
              >
                <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                  <div className="flex w-full min-w-0 items-start gap-2 sm:flex-1">
                    {unread ? (
                      // Three cues carry "unread", never colour alone (FR-015a):
                      // this dot, the card tint, and the bolder subject. The dot
                      // is the one AT can read, so it is labelled rather than
                      // aria-hidden.
                      <span
                        role="img"
                        aria-label={t('myCases.unread')}
                        className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary"
                      />
                    ) : null}
                    <p
                      className={`min-w-0 break-words text-sm text-foreground ${
                        unread ? 'font-bold' : 'font-medium'
                      }`}
                    >
                      {c.title !== null && c.title.trim().length > 0
                        ? c.title
                        : t('myCases.untitled')}
                    </p>
                  </div>
                  {/* 055 FR-011: subject + state only. The creation date and the
                      category stay in the projection but are not rendered here
                      (category nowhere, D-229(b); date only in ContactSummary). */}
                  <ConversationStatusPill status={c.status} closedAt={c.closedAt} size="row" />
                </div>
              </Link>
            </li>
          );
        })}
      </ul>

      {/* Polite live region for "N more loaded" / "all cases shown" announcements.
          Always present so injected text is announced (WCAG 4.1.3). */}
      <div role="status" aria-live="polite" className="sr-only">
        {announcement}
      </div>

      {loadMoreError ? (
        <p role="alert" className={`${noteClasses} mt-2`}>
          {t('myCases.loadMoreFailed')}
        </p>
      ) : null}

      {hasMore ? (
        <>
          <button
            type="button"
            className={loadMoreButtonClasses}
            onClick={() => loadMore?.()}
            // Never `disabled`: a disabled focused button is blurred (WCAG 2.4.3).
            // `loadMore` already ignores re-entry while a load is in flight.
            aria-busy={loadingMore}
          >
            {loadingMore ? t('myCases.loadingMore') : t('myCases.loadMore')}
          </button>
          {/* Enhancement-only sentinel for the IntersectionObserver (pointer
              users). Hidden from AT — the button is the accessible path. */}
          <div ref={sentinelRef} aria-hidden="true" />
        </>
      ) : everHadMoreRef.current ? (
        // Terminal state after real pagination — a focusable end-of-list status
        // so keyboard focus lands here when "Load more" is removed (FR-003).
        <p ref={endStatusRef} tabIndex={-1} className={`${noteClasses} mt-2`}>
          {t('myCases.allShown')}
        </p>
      ) : null}
    </>
  );
}
